// @ds/config/presentationRefs — cross-entity reuse for the presentation layer.
//
// The presentation layer (`@ds/config/presentation`) owns one field source per
// entity. These helpers let entity A reuse entity B's fields without
// re-authoring them:
//
//   - `toListColumn(field, placement?)` — a PresentationField → a DataTable
//     `ListColumnSpec` (display + deps), with optional placement knobs. Used to
//     drop another entity's field into a sub-table column array.
//   - `asDataSchema(presentation, meta)` — a whole PresentationSchema → the
//     runtime `Schema` shape (`ListColumnSpec` fields + non-enumerable `__meta`)
//     that `kind:"data"` sections (`runQuery`) consume for resource + select.
//   - `liftField(join, field, placement?)` — lift a SCALAR child field across a
//     parent FK (the presentation-native `pickRef`): the column reads
//     `parent[alias]` then delegates to the child field's `data`, and its deps
//     nest the child's scalars one hop under the relation alias.
//
// `toDeps`/`toListColumn` are the single conversion path: the transitional
// adapter (`presentationAdapter.ts`) imports them so there is exactly one
// PresentationField → ListColumnSpec translation.

import type { AnyColumnRef } from "./db/graph";
import type { ListColumnSpec } from "@ds/tables/types";
import type { Schema, SchemaField, SchemaMeta } from "./schema";
import {
  relationAlias,
  type PresentationField,
  type PresentationSchema,
  type RelationBinding,
} from "./presentation";

/** DataTable placement knobs that live at the consumer (column) site. */
export interface ColumnPlacement {
  width?: number | string;
  pinned?: "left" | "right";
  defaultVisible?: boolean;
  align?: "left" | "center" | "right";
  hideable?: boolean;
  sorter?: boolean;
  sortField?: string;
}

/** Legacy `deps` shape consumed by the select-string builder. */
interface LegacyDeps {
  fields?: string[];
  relations?: Record<string, string[]>;
  foreignKeys?: Record<string, { foreignKey: string; pk?: string }>;
}

/**
 * The columns + relation embeds a presentation field touches, in the legacy
 * `deps` shape (`fields` / `relations` / `foreignKeys`). Handles a single
 * `relation` AND a composite `relations[]` (e.g. a load+unload route).
 */
export function toDeps<P = unknown>(pf: PresentationField<P>): LegacyDeps | undefined {
  const rels: RelationBinding[] = [
    ...(pf.relation ? [pf.relation] : []),
    ...(pf.relations ?? []),
  ];
  if (rels.length) {
    const fields = new Set<string>();
    if (pf.column) fields.add(pf.column.column);
    pf.cols?.forEach((c) => fields.add(c.column));
    const relations: Record<string, string[]> = {};
    const foreignKeys: Record<string, { foreignKey: string; pk?: string }> = {};
    const single = rels.length === 1;
    for (const rel of rels) {
      const fk = rel.fk.column;
      // Alias precedence: binding `alias` (canonical) → legacy field `__alias`
      // on a single-relation field → derived from FK.
      const alias =
        rel.alias ?? (single && pf.__alias ? pf.__alias : relationAlias(fk));
      fields.add(fk);
      relations[alias] = rel.targetCols.map((c) => c.column);
      foreignKeys[alias] = { foreignKey: fk, ...(rel.pk ? { pk: rel.pk } : {}) };
    }
    return { fields: [...fields], relations, foreignKeys };
  }
  const fields = [
    ...(pf.column ? [pf.column.column] : []),
    ...(pf.cols?.map((c) => c.column) ?? []),
  ];
  return fields.length ? { fields } : undefined;
}

/** A PresentationField → a DataTable `ListColumnSpec` (+ optional placement).
 *  Generic in `<T, P>` so callers that pass the result into a
 *  `ListColumnSpec<T>[]` array don't need an `as any` cast at the use site. */
export function toListColumn<T = unknown, P = unknown>(
  pf: PresentationField<P>,
  placement?: ColumnPlacement,
): ListColumnSpec<T, P> {
  // `toDeps` derives from `column/cols/relation`. For schema-style fields
  // (`defineSchema`) that don't carry those but DO carry an explicit `deps`,
  // preserve it — otherwise the resulting column would have no fetch deps and
  // the SELECT would silently drop the column.
  const explicitDeps = (pf as { deps?: LegacyDeps }).deps;
  const out: Record<string, unknown> = {
    key: pf.key,
    labelKey: pf.labelKey,
    component: pf.component,
    props: pf.props,
    data: pf.data,
    sortable: pf.sortable,
    filterable: pf.filterable,
    deps: toDeps(pf) ?? explicitDeps,
    // Hand-declared Excel typing. Must travel: it is the only signal for cells
    // derivation can't read — a `render:` escape, or a number rendered through
    // the deliberately-untagged `TextValue`. Dropping it here silently disabled
    // the "an explicit `exportAs` always wins" escape hatch on this path.
    exportAs: (pf as { exportAs?: unknown }).exportAs,
  };
  if (pf.edit) {
    out.edit = {
      key: pf.edit.key,
      field: pf.edit.field,
      required: pf.edit.required,
      readOnly: pf.edit.readOnly,
      validate: pf.edit.validate,
    };
  }
  if (placement) Object.assign(out, placement);
  return out as unknown as ListColumnSpec<T, P>;
}

/**
 * A whole PresentationSchema → the runtime `Schema` shape a `kind:"data"` section
 * consumes: keyed `ListColumnSpec` fields + a non-enumerable `__meta` carrying
 * the resource. `meta.resource` MUST be the read resource (view) the rows come
 * from.
 */
export function asDataSchema<T>(
  presentation: PresentationSchema,
  meta: SchemaMeta,
): Schema<T> {
  const out: Record<string, SchemaField<T, unknown>> = {};
  for (const key of Object.keys(presentation)) {
    const pf = presentation[key];
    if (!pf) continue;
    out[key] = toListColumn(pf) as unknown as SchemaField<T, unknown>;
  }
  Object.defineProperty(out, "__meta", { value: meta, enumerable: false });
  return out as unknown as Schema<T>;
}

/** A FK hop from the parent table to a related entity. */
export interface LiftJoin {
  /** Parent's FK column to the related entity (`db.<parent>.<fk>`). */
  fk: AnyColumnRef;
  /** Embed alias; defaults to the FK column minus a trailing `_id`. */
  alias?: string;
}

/**
 * Lift a SCALAR child presentation field across a parent FK — the presentation
 * equivalent of legacy `pickRef(join, schemaField)`. The returned column reads
 * `parent[alias]` and delegates to the child field's `data`/`component`; its
 * deps nest the child's scalar columns one hop under the alias.
 *
 * Composite child fields (those embedding their own relations, e.g. a route)
 * are NOT supported here — no current consumer lifts one across a join. Render
 * such a field directly via `toListColumn` in a sub-table whose rows ARE the
 * child entity instead.
 */
export function liftField<TParent = unknown, P = unknown>(
  join: LiftJoin,
  field: PresentationField<P>,
  placement?: ColumnPlacement,
): ListColumnSpec<TParent, P> {
  const fk = join.fk.column;
  const alias = join.alias ?? relationAlias(fk);

  const childScalars = new Set<string>();
  if (field.column) childScalars.add(field.column.column);
  field.cols?.forEach((c) => childScalars.add(c.column));

  const childData = field.data;
  const out: Record<string, unknown> = {
    key: field.key,
    labelKey: field.labelKey,
    component: field.component,
    props: field.props,
    data: childData
      ? (parent: Record<string, unknown>) =>
          childData((parent[alias] ?? {}) as Record<string, unknown>)
      : undefined,
    sortable: false,
    deps: {
      relations: { [alias]: [...childScalars] },
      foreignKeys: { [alias]: { foreignKey: fk } },
    },
  };
  if (placement) Object.assign(out, placement);
  return out as unknown as ListColumnSpec<TParent, P>;
}
